<template>
  <div>
    <div class="panel-body">
      <div class="row mb-md">
        <div class="col-sm-2">
          <a @click="showAddForm = true" class="btn btn-primary" :class="{ 'disabled': showAddForm }"><i class="fa fa-plus"></i> Ajouter</a>
        </div>
        <div class="col-sm-4">
          <label style="margin-right:10px; width: 100%;">
            <input style="width: 100%;" v-bind:value="keywords" v-on:input="debounceSearch($event.target.value)" class="form-control" placeholder="Recherche" aria-controls="listing-datatable">
          </label>
        </div>
        <div class="col-sm-6 text-right">
          <Pagination
            :pageSize.sync="pageSize"
            :sizeOptions="sizeOptions"
            :selectedPageNum="pageNum"
            :totalRecords="totalRecords"
            v-on:change-page="changePage"
            :limit="2"
            :isLoading="isLoading"
            :disable="isLoading"
          >
          </Pagination>
        </div>
      </div>
      <div class="row" v-show="showAddForm">
        <div style="margin-bottom: -10px;">
          <strong>Ajouter un document</strong>
        </div>
        <DocumentEdit
         :is-new="true" 
         :show-cancel-button="true"
         v-on:save="addDocument"
         v-on:done="showAddForm = false" />
      </div>
      <div class="row">
        <div class="table-responsive">
          <div class="table-responsive">
            <GridGeneral
            :data="documents"
            :columns="gridColumns"
            :sortColumns="gridColumnsSort"
            :loadData="refresh">
              <template #document="slotProps">
                <div class="document-with-icon">
                  <a :href="slotProps.entry.document_url" target="_blank"><DocumentIcon :url="slotProps.entry.document_url" class="document-icon" />{{ slotProps.entry.title }}</a>
                </div>
              </template>
              <template #parent="slotProps">
                <template v-if="isAttachedServiceProviderOnly(slotProps.entry)">
                  <small class="text-muted">Non spécifié</small>
                </template>
                <template v-else>
                  <DocumentParentsSummary 
                    :linkToItems="true"
                    :document="slotProps.entry"
                    :dossierDetails="dossierDetails"
                    :serviceDetails="serviceDetails"
                    :ticketDetails="ticketDetails"
                  />
                </template>
              </template>
              <template #created_at="slotProps">
                <small class="text-muted text-nowrap">{{ moment(slotProps.entry.created_at).fromNow() }}</small>
              </template>
              <template #editlinks="slotProps">
                <div style="white-space: nowrap">
                  <a @click="$router.push({ name: 'document', params: { service_provider_id: $route.params.service_provider_id, document_id: slotProps.entry.document_id } })" style="margin-right: 20px; cursor:pointer">Modifier</a>
                  <a @click="confirmDocumentDelete(slotProps.entry.document_id)" style="cursor:pointer"><em class="fas fa-trash-alt fa-spacer" aria-hidden="true"></em></a>
                </div>
              </template>
            </GridGeneral>
            <div v-if="doneFirstLoad && documents.length === 0" style="padding: 40px 0; text-align: center;">
              <small class="text-muted">
                Aucun document n'a encore été ajouté à des items du fournisseur actuel.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <DocumentDeleteModal :document_id="documentIdToDelete" :documentToDelete="documentToDelete" :showModal="showDeleteModal" v-on:confirmDelete="deleteDocument" v-on:cancel="showDeleteModal = false" />
  </div>
</template>

<style lang="scss">
.document-with-icon {
  position: relative;
  padding-left: 20px;
  
  .document-icon {
    position: absolute;
    left: 0;
    top: 5px;
    transform: translateY(-50%);
  }
}

</style>

<script>
import axios from 'axios';
import Pagination from '@/components/Pagination.vue'
import GridGeneral from '@/components/GridGeneral.vue'
import DocumentEdit from '@/components/DocumentEdit.vue'
import DocumentIcon from '@/components/DocumentIcon.vue'
import DocumentDeleteModal from '@/components/DocumentDeleteModal.vue'
import DocumentParentsSummary from '@/components/DocumentParentsSummary.vue'

export default {
  components: {
    Pagination,
    GridGeneral,
    DocumentEdit,
    DocumentIcon,
    DocumentDeleteModal,
    DocumentParentsSummary
  },
  data: function() {
    return {
      documents:[],
      doneFirstLoad: false,
      dossierDetails: {},
      serviceDetails: {},
      ticketDetails: {},
      keywords: '',
      sort:'created_at',
      sortOrder:0,
      gridColumns: [
        {field:'document',title:'Document'},
        {field:'description',title:'Description'},
        {field:'parent',title:'En lien avec'},
        {field:'created_at',title:'Créé'},
        {field:'editlinks',title:'Modifications'}
      ],
      gridColumnsSort : ['document', 'created_at'],
      documentIdToDelete: null,
      showDeleteModal: false,
      showAddForm: false,
      pageSize: 50,
      sizeOptions: [20, 50, 100],
      pageNum: 1,
      totalRecords: 0,
      isLoading: false
    }
  },
  watch: {
    pageNum(newVal, oldVal) {
      if (+newVal !== +oldVal) {
        this.refresh();
        replaceUrlHash(this.prepareUrlHashToSet())
      }
    },
    pageSize(newVal, oldVal) {
      if (+newVal !== +oldVal) {
        this.refresh();
        replaceUrlHash(this.prepareUrlHashToSet())
      }
    }
  },
  methods: {
    debounceSearch(val){
      this.keywords = val;
      if(this.search_timeout) clearTimeout(this.search_timeout);
      
      const self = this;
      this.search_timeout = setTimeout(function() {
        self.refresh();
      }, 200);
    },

    refresh() {
      this.isLoading = true
      axios
        .get(this.$root.$data.server_base + '/api/service_provider_documents.php',
          { params: {
            service_provider_id: this.$route.params.service_provider_id,
            keywords: this.keywords,
            sort: this.sort,
            sortOrder: this.sortOrder,
            page:this.pageNum,
            page_size:this.pageSize
          }})
        .then(response => {
          if (!this.doneFirstLoad) { this.doneFirstLoad = true }
          this.documents = response.data.documents
          this.dossierDetails = response.data.dossier_details
          this.serviceDetails = response.data.service_details
          this.ticketDetails = response.data.ticket_details
          this.pageNum = response.data.page_num;
          this.totalRecords = response.data.total;
          this.isLoading = false
        })
    },
    
    addDocument(d) {
      this.saveDocument(d, () => {
        this.readmode = true
      })
    },
    
    saveDocument(d, callback) {
      let data = {
        service_provider_id: this.$route.params.service_provider_id
      }

      const fields = 'document_id url title description'.split(' ')

      fields.forEach(fieldName => {
        if (d[fieldName] !== undefined) {
          data[fieldName] = d[fieldName]
        }
      })

      axios
        .post(this.$root.$data.server_base + '/api/document_save.php',
          data,
          {headers:{
              'Content-Type': 'application/json',
          }})
        .then(() => {
          this.refresh()
          if (callback !== undefined && typeof callback === 'function') {
            callback()
          }
        })
    },
    
    confirmDocumentDelete(document_id) {
      this.documentIdToDelete = document_id
      this.showDeleteModal = true
    },
    
    deleteDocument(document_id) {
      var data = {
        service_provider_id: this.$route.params.service_provider_id,
        document_id:this.documentIdToDelete
      }
      
      axios
        .post(this.$root.$data.server_base + '/api/document_delete.php',
          data,
          {headers:{
              'Content-Type': 'application/json',
        }})
        .then(response => {
          this.documentIdToDelete = null
          this.showDeleteModal = false
          this.refresh();
        })
    },
    
    isAttachedServiceProviderOnly(document) {
      return !(document.designation_dossier_id_list.length > 0 || document.service_id_list.length > 0 || document.ticket_id_list.length > 0 || document.requirement_num_list.length > 0)
    },

    changePage: function(pageNum)
    {
      this.pageNum = pageNum
    },

    prepareUrlHashToSet: function() {
      return `page=${this.pageNum}&pageSize=${this.pageSize}`
    },

    readPaginationFromUrlHashAndRefresh: function() {
      this.preventRefresh = true;
      this.$nextTick(() => {
        this.setSelectedPageNumFromUrlHash()
        this.setSelectedPageSizeFromUrlHash()
        this.preventRefresh = false;
        this.refresh();
      })
    },

    setSelectedPageNumFromUrlHash: function() {
      var pageNumFromUrlHash = getHashParameterByName('page')
      if (!pageNumFromUrlHash || pageNumFromUrlHash === '') {
        return
      }
      this.pageNum = pageNumFromUrlHash
    },

    setSelectedPageSizeFromUrlHash: function() {
      var pageSizeFromUrlHash = getHashParameterByName('pageSize')
      if (!pageSizeFromUrlHash || pageSizeFromUrlHash === '') {
        return
      }
      this.pageSize = pageSizeFromUrlHash
    }
  },
  computed: {
    documentToDelete() {
      if (this.documentIdToDelete === null) return null
      return this.documents.find((d) => d.document_id == this.documentIdToDelete)
    }
  },
  mounted() {
    this.readPaginationFromUrlHashAndRefresh();
    this.refresh()
  }
}

function replaceUrlHash(newHash) {
  if ((''+newHash).charAt(0) !== '#') newHash = '#' + newHash;
  history.replaceState('', '', newHash);
}

// https://stackoverflow.com/a/901144/2414273
function getHashParameterByName(name, hash) {
  if (!hash) hash = window.location.hash;
  name = name.replace(/[\[\]]/g, '\\$&');
  var regex = new RegExp('[#&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(hash);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}
</script>